import React, { useCallback, useContext, useMemo } from 'react';
import { KodplanContext } from '../../../../../../../Components/KodplanManager/Context/KodplanContext';
import KonteringContext from '../../../../../_Common/Components/KonteringManager/KonteringContext';
import { definedSelectedId } from '../../../../../../../Components/MasterDetail/MasterDetailUtils';
import { IBusinessCollectionProps } from '../../../../../../../Types/ObjektVy';
import { AssociationList } from '../../../../../_Common/Components/BusinessCollectionComponents/AssociationList/AssociationList';
import { useBusinessDataFormContext } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import { GridContext } from '../../../../../../GridContext';
import { NEWLY_ADDED_ID_PREFIX_FOR_UPDATE } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Const/BusinessDataFormConst';
import BokforderContext from '../BokforderContext/BokforderContext';
import { BokforderType__Associations_Rader } from '../../Queries/_QueryTypes/BokforderType';
import ImportDialog from '../ImportDialog/ImportDialog';
import { BokforderWrapperContext } from '../../../../Rutiner/Bokforder/Contexts/BokforderWrapperContext';
import { EKBokforderSysoptionQuery_db_SYSOptions_All } from '../../../../Rutiner/Bokforder/Queries/_QueryTypes/EKBokforderSysoptionQuery';
import { EK_USE_IMPORT_FROM_CLIPBOARD } from '../../../../Rutiner/Bokforder/Const/BokforderSysoptionConsts';
import { useUtanordningAssociationListConfig } from './Utils/UtanordningGridUtils';
import {
  useBokforderImportFromClipboardOnCreateCallback,
  useBokforderImportFromClipboardSelectableColumnHeaders,
} from '../BokforderGrid/Utils/BokforderGridUtils';
import { IValues } from '../../../../../_StandardRutin/Mutations/ObjectMutations/ObjectUpdateMutationBase';
import { isRowEmpty } from '../../Utils/BokforderUtils';
import { TEDEKBokforderVariantEnum } from '../../../../../../../globalQueryTypes';

interface IProps extends IBusinessCollectionProps {
  editKontering: boolean;
  setEditKontering: React.Dispatch<React.SetStateAction<boolean>>;
}

export const UtanordningGridView: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const bokforderContext = useContext(BokforderContext);
  const bokforderContextMakeNewRow = bokforderContext.makeNewRow;
  const bokforderContextSetNewRowIndex = bokforderContext.setNewRowIndex;
  const bokforderContextImportDialogOpen = bokforderContext.importDialogOpen;
  const bokforderContextSetImportDialogOpen =
    bokforderContext.setImportDialogOpen;
  const bokforderWrapperContext = useContext(BokforderWrapperContext);
  const formContext = useBusinessDataFormContext();
  const formContextUpdate = formContext?.update;
  const konteringContext = useContext(KonteringContext);
  const kodplaner = useContext(KodplanContext);

  const ekUseImportFromClipboard: boolean = useMemo(() => {
    return (
      bokforderWrapperContext.sysOptions?.find(
        (option: EKBokforderSysoptionQuery_db_SYSOptions_All) =>
          option?.OptionKey === EK_USE_IMPORT_FROM_CLIPBOARD
      )?.OptionData === 'Y'
    );
  }, [bokforderWrapperContext.sysOptions]);

  const allRows: BokforderType__Associations_Rader[] =
    formContext?.formState.currentValues?._Associations?.Rader ?? [];

  const createNewRow = useCallback(
    (index: number, newValues?: IValues) => {
      formContextUpdate?.(
        bokforderContextMakeNewRow(
          `${NEWLY_ADDED_ID_PREFIX_FOR_UPDATE}Bokforder_Rad_${index}`,
          newValues
        ),
        true
      );
    },
    [formContextUpdate, bokforderContextMakeNewRow]
  );

  const createNewEmptyRow = useCallback(() => {
    createNewRow(bokforderContext.newRowIndex);
    bokforderContextSetNewRowIndex(prevIndex => prevIndex + 1);
  }, [
    bokforderContext.newRowIndex,
    bokforderContextSetNewRowIndex,
    createNewRow,
  ]);

  const lastRow: BokforderType__Associations_Rader = useMemo(() => {
    return (
      formContext?.formState.currentValues?._Associations?.Rader?.[
        formContext?.formState.currentValues?._Associations?.Rader?.length - 1
      ] ?? {}
    );
  }, [formContext?.formState.currentValues?._Associations?.Rader]);

  // Skapa en ny tom rad om det inte finns några rader eller om sista raden har något ifyllt
  if (
    !kodplaner.loading &&
    kodplaner.activeKodplan &&
    !formContext?.formState.loading &&
    (bokforderContext.isEditMode || props.editKontering) &&
    (allRows.length === 0 ||
      (!isRowEmpty(lastRow) &&
        bokforderContext.bokforderVariant !==
          TEDEKBokforderVariantEnum.UTANORDNINGTILLGODO))
  ) {
    createNewEmptyRow();
  }

  const id = props.id ? props.id : '';

  const utanordningAssociationListConfig = useUtanordningAssociationListConfig({
    editKontering: props.editKontering,
  });

  const selectableColumnHeaders =
    useBokforderImportFromClipboardSelectableColumnHeaders();

  const onImportCreateCallback =
    useBokforderImportFromClipboardOnCreateCallback();

  return (
    <>
      {formContext?.formState.loading ||
      konteringContext.konteringValidationQueryLoading ? (
        <>Loading...</>
      ) : (
        <GridContext.Provider value={{ inGrid: true }}>
          <AssociationList
            associationName="Rader"
            associationPath="Rader"
            collectionName={'EKBokforder'}
            ownId={definedSelectedId(id)}
            config={utanordningAssociationListConfig}
          />
        </GridContext.Provider>
      )}

      {ekUseImportFromClipboard && (
        <ImportDialog
          associationName="Rader"
          collectionName="EKBokforder"
          open={bokforderContextImportDialogOpen}
          selectableColumnHeaders={selectableColumnHeaders}
          setOpen={bokforderContextSetImportDialogOpen}
          onCreateCallback={onImportCreateCallback}
          invalidValuesInfoText="Orangemarkerade värden kommer ej att läsas in i bokföringsordern"
        />
      )}
    </>
  );
};
